import { useCallback, useEffect, useState } from 'react';
import Parse from '../api/config';
import { useAuth } from '../components/AuthContext';
import { isArchived } from '../components/ui';

// The role queues call the event's date `date`; isArchived expects the field
// names the Form itself uses.
export const archivable = (form) => ({
    dayDateOfEvent: form.date,
    occurrenceDates: form.occurrenceDates,
});

// Everything waiting on the signed-in person, across every role they hold.
// The sidebar badge, the dashboard queue, and the Approvals page all read
// from here so they can never disagree about what needs attention.
export const useApprovalQueue = () => {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!user?.email) {
            setItems([]);
            setRoles([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const { roles: userRoles = [] } = await Parse.Cloud.run('getUserRoles', { email: user.email });
            setRoles(userRoles);

            const perRole = await Promise.all(
                userRoles.map(async (role) => {
                    try {
                        const { forms = [] } = await Parse.Cloud.run('getPendingFormsForRole', { role });
                        return forms.map((form) => ({ ...form, role }));
                    } catch (error) {
                        console.warn(`Could not load pending forms for role ${role}:`, error);
                        return [];
                    }
                })
            );

            // One form can need two of your roles; show it once, listing both.
            const byForm = new Map();
            for (const entry of perRole.flat()) {
                const existing = byForm.get(entry.id);
                if (existing) {
                    existing.roles.push(entry.role);
                } else {
                    byForm.set(entry.id, { ...entry, roles: [entry.role] });
                }
            }

            // An event that finished weeks ago isn't waiting on anyone. Approving
            // it changes nothing, so it shouldn't sit in the queue forever.
            setItems([...byForm.values()].filter((form) => !isArchived(archivable(form))));
        } catch (error) {
            console.error('Could not load your approval queue:', error);
            setItems([]);
            setRoles([]);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { items, roles, loading, refresh, count: items.length };
};
